import * as React from "react";
import { cn } from "../lib/utils.js";
import { Button } from "./button.js";

/**
 * RecoveryView (CAP-6): an inline card for any failure. It renders the
 * message, the impact line when there is one, and up to three actions, and
 * always shows the reference on `contact_support`. Every string arrives
 * translated; react-ui holds no copy. It sits in a polite live region and
 * never clears the surrounding form.
 */

export interface RecoveryViewAction {
  id: string;
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Unavailable action: rendered aria-disabled with this visible reason. */
  disabledReason?: string;
}

export interface RecoveryViewProps extends Omit<React.ComponentProps<"section">, "title"> {
  message: string;
  impact?: string;
  actions: RecoveryViewAction[];
  /** Fully translated reference line, e.g. "Reference: 9f3c…". */
  reference?: string;
  kind: "recoverable" | "nonRecoverable";
  /**
   * Own polite live region (default). Pass false when the caller already
   * wraps it in a persistent live region, to avoid double announcements.
   */
  live?: boolean;
}

function RecoveryView({
  message,
  impact,
  actions,
  reference,
  kind,
  live = true,
  className,
  ...props
}: RecoveryViewProps) {
  const shown = actions.slice(0, 3);
  return (
    <section
      data-slot="recovery-view"
      data-kind={kind}
      aria-live={live ? "polite" : undefined}
      className={cn(
        "rounded-lg border border-status-error bg-card p-4 text-card-foreground",
        className,
      )}
      {...props}
    >
      <p className="font-semibold">{message}</p>
      {impact ? <p className="mt-1 text-sm text-muted-foreground">{impact}</p> : null}
      {reference ? (
        <p className="mt-2 text-sm text-muted-foreground">
          <span className="font-mono">{reference}</span>
        </p>
      ) : null}
      {shown.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {shown.map((action, i) => {
            const variant = i === 0 ? "default" : "outline";
            const reasonId = action.disabledReason ? `${action.id}-reason` : undefined;
            if (action.href && !action.disabledReason) {
              return (
                <Button key={action.id} asChild variant={variant} size="touch">
                  <a href={action.href}>{action.label}</a>
                </Button>
              );
            }
            return (
              <span key={action.id} className="inline-flex flex-col">
                <Button
                  type="button"
                  variant={variant}
                  size="touch"
                  aria-disabled={action.disabledReason ? true : undefined}
                  aria-describedby={reasonId}
                  onClick={() => {
                    if (!action.disabledReason) action.onSelect?.();
                  }}
                >
                  {action.label}
                </Button>
                {action.disabledReason ? (
                  <span id={reasonId} className="mt-1 text-xs text-muted-foreground">
                    {action.disabledReason}
                  </span>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

export { RecoveryView };
